import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useAuthStore,
    useCompanySettingsStore,
    useSDKStore,
    useUIStore,
} from '../../../hooks/use_global_context';
import appLogger from '../../../util/logging/app_logger';
import { streem } from 'streem-sdk-protobuf';
import type { StatusOptionValueType } from '../status_option_value';
import {
    StreemAPI,
    StreemApiUpdateUserAvailabilityRequestAvailabilityStatus,
    StreemApiUserAvailabilityAvailabilityStatus,
} from '@streem/api';
import { ToastTypes } from '@streem/ui-react';
import { defaultNotAvailableOption } from '../util/default_availability_selection';
import {
    findOptionThatMatchesWallStatus,
    getOptionFromApiStatus,
    mapWallStatusToApiRequestStatus,
} from '../util/expert_availability_utils';
import { contextValues } from '@streem/logger';
import { useMatchUserAvailabilityStatus } from '../../../hooks/use_match_availability_status_hook';

const AvailabilityStatus = streem.api.AvailabilityStatus;

type ExpertAvailabilityReturnValues = {
    currentAvailabilityStatus: number;
    isOffline: boolean;
    isSwitchingWorkspaces: boolean;
    isUpdatingSelection: boolean;
    selectedAvailabilityStatus: Readonly<StatusOptionValueType>;
};

type ExpertAvailabilityReturnFunctions = {
    updateUserAvailability: (
        status: streem.api.AvailabilityStatus,
    ) => Promise<StreemApiUserAvailabilityAvailabilityStatus>;
    resetUserAvailabilityToOffline: () => Promise<void>;
    onReloadHandler: () => Promise<void>;
};

type ExpertAvailabilityReturnArray = [
    ExpertAvailabilityReturnValues,
    ExpertAvailabilityReturnFunctions,
    React.Dispatch<React.SetStateAction<Readonly<StatusOptionValueType>>>,
];

const log = appLogger.extend('UseExpertAvailability');

export const useExpertAvailability = (): ExpertAvailabilityReturnArray => {
    const sdkStore = useSDKStore();
    const companySettingsStore = useCompanySettingsStore();
    const authStore = useAuthStore();
    const uiStore = useUIStore();

    // prevents need for stores in dependency array
    const authStoreRef = useRef(authStore);
    const uiStoreRef = useRef(uiStore);

    const [isUpdatingSelection, setUpdatingFromSelection] = useState(false);
    const [selectedAvailabilityStatus, setSelectedStatus] = useState(defaultNotAvailableOption);
    const [isSwitchingWorkspaces, setIsSwitchingWorkspaces] = useState(false);

    const availabilityStatusWallItem = useMatchUserAvailabilityStatus(
        sdkStore?.userAvailabilityStatus?.current?.availabilityStatus ===
            AvailabilityStatus.AVAILABILITY_STATUS_OFFLINE,
    );

    const currentAvailabilityStatus = availabilityStatusWallItem?.current?.availabilityStatus;
    const isOffline = currentAvailabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_OFFLINE;

    useEffect(() => {
        if (currentAvailabilityStatus != null) {
            // When the status is INVALID just set it to NOT_READY
            const matchedStatus = findOptionThatMatchesWallStatus(currentAvailabilityStatus);

            log.info(
                `Updating availability status (${AvailabilityStatus[currentAvailabilityStatus]}) to matched status (${matchedStatus.value.label})`,
            );
            setSelectedStatus(matchedStatus);
        }
    }, [currentAvailabilityStatus]);

    const updateUserAvailability = useCallback(
        async (status: streem.api.AvailabilityStatus) => {
            const option = getOptionFromApiStatus(status);
            if (option.value.key !== selectedAvailabilityStatus.value.key) {
                const newStatus = mapWallStatusToApiRequestStatus(status);
                // block interaction
                setUpdatingFromSelection(true);
                // immediately update the UI for the intended selection if needed
                log.info(
                    `Immediately updating selected option from "${selectedAvailabilityStatus.label}" to "${option.label}"`,
                );
                setSelectedStatus(option);

                try {
                    const response = await StreemAPI.users.updateUserAvailability(
                        authStore.userId,
                        {
                            availabilityStatus: newStatus,
                        },
                    );
                    log.info(`Updated availability status via API to: ${newStatus}`);
                    return response?.userAvailability?.availabilityStatus;
                } catch (e) {
                    // revert UI selection on error
                    const revertedOption =
                        findOptionThatMatchesWallStatus(currentAvailabilityStatus);
                    setSelectedStatus(revertedOption);
                    log.error(
                        `Error updating user availability status via API to: ${newStatus}, reverted selected option to: ${revertedOption?.label}`,
                        e,
                    );
                    uiStoreRef.current.addToastWithManualClose({
                        content: 'There was an error changing your status',
                        flavor: ToastTypes.ERROR,
                        id: 'user-availability-status-update-fail',
                    });
                } finally {
                    setUpdatingFromSelection(false);
                }
            }
        },
        [
            authStore.userId,
            currentAvailabilityStatus,
            selectedAvailabilityStatus.label,
            selectedAvailabilityStatus.value.key,
        ],
    );

    const resetUserAvailabilityToOffline = useCallback(async () => {
        setIsSwitchingWorkspaces(true);
        if (
            companySettingsStore.expertAvailabilityEnabled &&
            [
                AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE,
                AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE,
            ].includes(currentAvailabilityStatus) &&
            authStore.userId
        ) {
            try {
                await StreemAPI.users.updateUserAvailability(authStore.userId, {
                    availabilityStatus:
                        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.OFFLINE,
                });
                log.setContextValue(contextValues.USER_AVAILABILITY, {
                    status: StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.OFFLINE,
                    label: null,
                });
                log.info(`Updated Availability Status to OFFLINE on workspace switch`);
            } catch (e) {
                log.error(
                    'There was an error changing the user availability status on workspace switch',
                    e,
                );
            } finally {
                setIsSwitchingWorkspaces(false);
            }
        }
    }, [
        authStore.userId,
        companySettingsStore.expertAvailabilityEnabled,
        currentAvailabilityStatus,
    ]);

    const onReloadHandler = useCallback(async () => {
        try {
            log.info(
                `Updating status to ${StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.NOTAVAILABLE} before reloading the page`,
            );

            await StreemAPI.users.updateUserAvailability(authStore.userId, {
                availabilityStatus:
                    StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.NOTAVAILABLE,
            });

            log.info('Update was successful, reloading the page to re-establish connection');
            window.location.reload();
        } catch (error) {
            log.error(
                'Failed to to update availability status on reload, logging user out.',
                error,
            );
            authStoreRef.current.logout();
        }
    }, [authStore.userId]);

    const state: ExpertAvailabilityReturnValues = useMemo(
        () => ({
            currentAvailabilityStatus,
            selectedAvailabilityStatus,
            isUpdatingSelection,
            isOffline,
            isSwitchingWorkspaces,
        }),
        [
            currentAvailabilityStatus,
            isOffline,
            isSwitchingWorkspaces,
            isUpdatingSelection,
            selectedAvailabilityStatus,
        ],
    );

    const actions: ExpertAvailabilityReturnFunctions = useMemo(
        () => ({
            updateUserAvailability,
            resetUserAvailabilityToOffline,
            onReloadHandler,
        }),
        [onReloadHandler, resetUserAvailabilityToOffline, updateUserAvailability],
    );

    return [state, actions, setSelectedStatus];
};
